import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "twitter",
  app: "X",
  title: "How to block X (Twitter) on iPhone and stay off it",
  h1: "How to block X, formerly Twitter",
  description:
    "Screen Time steps, why the timeline refreshes under your thumb, and what to do about an app that makes you angry and calls it being informed.",
  targets: [
    "how to block twitter on iphone",
    "block x app iphone",
    "stop using twitter",
    "how to quit x app",
  ],
  published: "2026-09-03",
  standfirst:
    "The distinctive thing about X is not that it wastes time. It is that it reliably makes people angrier than they were, and calls that being informed.",
  takeaways: [
    "The timeline updates while you read it, so there is never a state of having finished — the page you left is not the page you return to five minutes later.",
    "Outrage is the most reliably shared emotion on the platform, which means the ranking system finds it whether or not anybody intends that.",
    "Blocking the app without blocking the website changes nothing; X works in a browser and the habit moves there immediately.",
  ],
  mechanic: [
    "X is the only app on this list whose content changes faster than you can read it. A feed of videos is infinite but static — the thing you saw is still the thing that was there. On X the timeline is reordered continuously, so refreshing is rewarded, and there is no reachable state of having caught up. The app is built so that finishing is not a thing that can happen.",
    "The second mechanic is emotional rather than structural. Engagement-ranked feeds surface whatever produces a reaction, and the reaction that reliably travels furthest is indignation — not because anybody designed for anger specifically, but because anger is what people quote, reply to and share. The result is an app that takes a person with ordinary opinions and returns them, twenty minutes later, in a fight with a stranger about something that will not matter on Thursday.",
    "And the notifications are social rather than informational. A like, a reply, a quote — each one is a small social event with your name attached, which is a considerably stronger pull than any recommendation engine, because it is about you.",
  ],
  limitFails:
    "An app limit fails here for the same reason it fails on Reddit — x.com works in a browser and the habit relocates in an afternoon — but it fails first for a different reason. X is where a lot of people follow news, so the limit gets an exception on any day something is happening, and something is always happening. The exception is not dishonest; it is exactly how the app keeps its place, by being genuinely useful about four times a year and behaving as though it is that useful every day.",
  instead: [
    "Block the site as well as the app: Settings → Screen Time → Content & Privacy Restrictions → Web Content → Never Allow, and add x.com and twitter.com.",
    "Turn off every notification except direct messages. The social notifications are the strongest hook on the platform and they are almost never urgent.",
    "Get news somewhere with an end: a newsletter, a front page, anything published in editions rather than continuously.",
    "If you post for work, separate the posting from the reading — schedule from a browser and do not open the timeline while you are there.",
  ],
  verses: [
    {
      verse: {
        text: "So, then, my beloved brothers, let every man be swift to hear, slow to speak, and slow to anger; for the anger of man doesn't produce the righteousness of God.",
        ref: "James 1:19–20",
      },
      note: "A sequence with speeds attached, and X inverts all three: it is built for speaking first, at volume, immediately. The second half is the sentence to sit with — not that anger is forbidden, but that it does not produce what the angry person believes it is producing. Nobody has ever been argued into anything at that speed.",
    },
    {
      verse: {
        text: "For where jealousy and selfish ambition are, there is confusion and every evil deed.",
        ref: "James 3:16",
      },
      note: "Confusion is the word — disorder, instability, things out of place. James is describing what happens to a community rather than to an individual, which is the more interesting claim: rivalry does not stay private, it reorganises how a group behaves, and a public timeline optimised for reaction is a fairly literal illustration of the mechanism.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "There is no caught up",
      id: "there-is-no-caught-up",
    },
    {
      type: "p",
      text: "Most compulsive checking is a search for a finished state. Email can be emptied. A newspaper ends. X is specifically constructed so that the state you are looking for does not exist, and the refresh is rewarded every time.",
    },
    {
      type: "p",
      text: "That is not a criticism of the design so much as a description of it, and it is why time limits feel arbitrary here in a way they do not elsewhere: there is no natural stopping point to align them with.",
    },
    {
      type: "h2",
      text: "Notice what it does to your mood",
      id: "notice-what-it-does-to-your-mood",
    },
    {
      type: "p",
      text: "The useful measurement is not minutes. It is whether you are more or less pleasant to live with in the twenty minutes afterwards, and most people already know the answer and have never said it out loud.",
    },
    {
      type: "note",
      text: "Ask someone in your house. They will have noticed, and they will be able to tell you which app it is without being told what the question is about.",
    },
    {
      type: "h2",
      text: "News with an edge on it",
      id: "news-with-an-edge-on-it",
    },
    {
      type: "p",
      text: "The strongest argument for keeping the app is staying informed, and it is worth taking seriously rather than waving away. But being informed by a continuous outrage-ranked stream is not the same as being informed, and the alternative — anything published in editions, which ends — is both older and better.",
    },
    {
      type: "p",
      text: "It also removes the part where you carry the argument into the evening, which is where {the real cost} of this app is paid.",
      links: {
        "the real cost": "/prayers/prayer-for-anger",
      },
    },
  ],
  faq: [
    {
      q: "How do I block X on my iPhone?",
      a: "Block the app through Screen Time App Limits and the site through Content & Privacy Restrictions → Web Content → Never Allow, adding both x.com and twitter.com. Without the second, the habit moves to Safari within a day.",
    },
    {
      q: "Why does X make me angry?",
      a: "Because engagement ranking surfaces what people react to, and indignation is what travels furthest. It is a structural property of the ranking rather than anybody's intention, which is why it does not go away by following nicer accounts.",
    },
    {
      q: "How do I stay informed without X?",
      a: "Use something published in editions rather than continuously — a newsletter, a front page, a weekly. The thing you lose is immediacy; the thing you gain is a stopping point, which is what the app has removed.",
    },
  ],
  relatedApps: ["reddit", "facebook"],
  related: ["screen-time-limits-dont-work", "how-to-stop-scrolling-at-night"],
  relatedPrayers: ["prayer-for-anger", "prayer-for-focus"],
};
